import type { FieldDefinition } from '@sentra/contracts';
import { IMAGES_COMPLETENESS_KEY } from '@sentra/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';
import { labelOf } from '@/features/persons/form/values';
import { useLocale } from '@/i18n';

/**
 * The completeness warning: what this record is missing, and a question rather than a refusal.
 *
 * **It never blocks.** Section 1.2's first behavioural example is an operator registering somebody
 * with only a name, being warned, and continuing; the server says so too by answering
 * `confirmation_required` as a 200 rather than as an error. The confirm button is the primary
 * action and it is not disabled, because the whole point is that an incomplete registration is a
 * legitimate outcome — somebody is standing at a desk without their documents, and refusing to
 * record them is worse than recording them incompletely.
 *
 * **It names the fields, in the operator's language, from the live registry.** A list of machine
 * keys would be a dialog people learn to confirm without reading. `images` is not a field and has no
 * definition, so it is named by the one catalogue key that covers it.
 *
 * **A key with no definition is still listed, by its key.** That happens when an administrator
 * retires a field the warning still watches, and dropping it silently would make the dialog say the
 * record is missing fewer things than the server thinks it is — which is exactly the direction this
 * warning must never be wrong in.
 */
export function CompletenessDialog({
  open,
  missing,
  definitions,
  confirming,
  onConfirm,
  onCancel,
}: Readonly<{
  open: boolean;
  /** The field keys the server reported as missing, plus possibly the images token. */
  missing: readonly string[];
  definitions: readonly FieldDefinition[];
  /** True while the confirmed submission is in flight, so the button cannot be pressed twice. */
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();

  function nameOf(key: string): string {
    if (key === IMAGES_COMPLETENESS_KEY) {
      return t('person.form.missingPhotograph');
    }
    const definition = definitions.find((candidate) => candidate.key === key);
    return definition === undefined ? key : labelOf(definition, locale);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent closeLabel={t('common.close')} data-testid="completeness-dialog">
        <DialogHeader>
          <DialogTitle>{t('person.form.completenessTitle')}</DialogTitle>
          <DialogDescription>{t('person.form.completenessBody')}</DialogDescription>
        </DialogHeader>

        <ul className="list-disc pl-5 text-sm" data-testid="completeness-missing">
          {missing.map((key) => (
            <li key={key}>{nameOf(key)}</li>
          ))}
        </ul>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('person.form.completenessBack')}
          </Button>
          <Button type="button" disabled={confirming} onClick={onConfirm}>
            {t('person.form.completenessConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
